import React, { useState, useEffect, useCallback } from 'react';
import { jsPDF } from 'jspdf';

/* ===========================================================================
   The tape is built twice: once in CSS/SVG for the screen, once in jsPDF
   drawing primitives for the PDF. jsPDF has no relationship to the CSS, so
   nothing carries over automatically.

   Everything the two renderings must agree on lives in this block, in
   proportions rather than absolute units, so they read as the same object.
   Screen values are px against a 320px tape; PDF values are inches against a
   3in tape. 320px : 3in is the conversion factor for every pair below.
   =========================================================================== */

/** Characters per line. Both renderings wrap with wrapMono() at this measure. */
export const TAPE_CHARS = 34;

/**
 * Above this quantity the tally marks stop being countable and turn into
 * texture, so we print the numeral alone. Space is not the constraint here
 * (twenty marks still fits the measure); legibility is.
 */
export const TALLY_CAP = 12;

const SCREEN_TAPE_W = 320;
const PDF_TAPE_W = 3.0;
/** px -> in, for keeping the two renderings proportional. */
const PX = PDF_TAPE_W / SCREEN_TAPE_W;

/** Tally geometry. `lg` is the wordmark lockup, `sm` is the per-line-item mark. */
export const TALLY = {
  screenSm: { h: 13, pitch: 3.8, gap: 5.5, width: 1.6, over: 1.6 },
  screenLg: { h: 20, pitch: 5.8, gap: 8.5, width: 2.4, over: 2.4 },
};
const scaleGeom = (g) => ({
  h: g.h * PX,
  pitch: g.pitch * PX,
  gap: g.gap * PX,
  width: g.width * PX,
  over: g.over * PX,
});
TALLY.pdfSm = scaleGeom(TALLY.screenSm);
TALLY.pdfLg = scaleGeom(TALLY.screenLg);

/** Type scale, px on screen. The PDF uses the same values through PT(). */
const TS = {
  body: 13,
  small: 11.5,
  label: 11,
  word: 12,
  kicker: 9.5,
  total: 15,
  fine: 10,
};
/** px on a 320px tape -> pt on a 3in tape. */
const PT = (px) => px * PX * 72;

/* --- Ink, as jsPDF 0-255 triples. Mirrors the paper tokens in index.css. --- */
const INK = [26, 26, 26];
const INK_SOFT = [107, 107, 107];
const INK_FAINT = [155, 155, 155];
const RULE = [216, 214, 208];
const TERRACOTTA = [217, 119, 87]; // #D97757

/* --- PDF page geometry ---------------------------------------------------- */
const PAGE_W = 8.5;
const PAGE_H = 11;
const TAPE_X = (PAGE_W - PDF_TAPE_W) / 2;
/** 26px of screen padding, in tape-relative units. Must stay in step with the
    padding on .perf-paper, or 34 characters no longer fits the measure. */
const TAPE_PAD = 26 * PX;
const CONTENT_X = TAPE_X + TAPE_PAD;
const CONTENT_R = TAPE_X + PDF_TAPE_W - TAPE_PAD;
// The measure between them is 2.51in, which holds exactly TAPE_CHARS
// characters of Courier at PT(TS.body). Changing either end breaks the wrap.
const TAPE_TOP = 0.55;
const TAPE_BOTTOM = PAGE_H - 0.55;
const PERF_R = 6 * PX;
const PERF_PITCH = 16 * PX;

/**
 * Wrap text to a fixed character measure.
 *
 * Used by BOTH renderings rather than letting CSS wrap the screen and
 * splitTextToSize wrap the PDF, so descriptions break at identical points in
 * both. This is most of what makes the two look like one object.
 */
export function wrapMono(text, chars = TAPE_CHARS) {
  const words = String(text == null ? '' : text).split(/\s+/).filter(Boolean);
  if (!words.length) return [];
  const lines = [];
  let line = '';
  for (const word of words) {
    if (!line) line = word;
    else if (line.length + 1 + word.length <= chars) line += ' ' + word;
    else {
      lines.push(line);
      line = word;
    }
    while (line.length > chars) {
      lines.push(line.slice(0, chars));
      line = line.slice(chars);
    }
  }
  if (line) lines.push(line);
  return lines;
}

/**
 * Lay out tally marks: groups of five as four uprights with a diagonal struck
 * through them, then singles. Returns unit-space segments normalised so the
 * leftmost point (the diagonal's overhang) sits at x=0.
 */
export function tallyMarks(count, g) {
  const n = Math.floor(Math.max(0, Number(count) || 0));
  if (!n) return { segments: [], width: 0, height: g.h };

  const segments = [];
  let x = 0;
  let left = n;
  while (left > 0) {
    const inGroup = Math.min(left, 5);
    const uprights = inGroup === 5 ? 4 : inGroup;
    for (let i = 0; i < uprights; i++) {
      const ux = x + i * g.pitch;
      segments.push([ux, 0, ux, g.h]);
    }
    if (inGroup === 5) {
      segments.push([
        x - g.over,
        g.h - g.over,
        x + 3 * g.pitch + g.over,
        g.over,
      ]);
    }
    x += (uprights - 1) * g.pitch + g.gap + (inGroup === 5 ? g.over : 0);
    left -= inGroup;
  }

  let min = Infinity;
  let max = -Infinity;
  segments.forEach(([x1, , x2]) => {
    min = Math.min(min, x1, x2);
    max = Math.max(max, x1, x2);
  });
  return {
    segments: segments.map(([x1, y1, x2, y2]) => [x1 - min, y1, x2 - min, y2]),
    width: max - min,
    height: g.h,
  };
}

/** Whether a quantity gets marks at all. Fractions fall back to the numeral. */
export const showsTally = (qty) => {
  const n = Number(qty) || 0;
  return n > 0 && n <= TALLY_CAP && Number.isInteger(n);
};

/**
 * Currency is stored as the symbol itself, not a locale code, so the two
 * renderings print exactly the same characters with no lookup between them.
 */
export const CURRENCIES = [
  { code: 'USD', symbol: '$' },
  { code: 'CAD', symbol: 'CA$' },
  { code: 'EUR', symbol: '\u20AC' },
  { code: 'GBP', symbol: '\u00A3' },
  { code: 'AUD', symbol: 'A$' },
];

/**
 * Separators are pinned to en-US so the PDF and the screen never disagree,
 * but the symbol is whatever the user chose. A bare "575.00" on an invoice
 * crossing a border is a real question for the person receiving it.
 */
export const money = (n, symbol = '') =>
  `${symbol}${(Number(n) || 0).toLocaleString('en-US', {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  })}`;

/**
 * The one promotional line on the artifact, and the only thing the watermark
 * toggle removes. Defined once because the screen and the PDF must print the
 * same characters.
 *
 * The label and the link target must name the SAME host. This line lands in a
 * client's inbox on a financial document, and visible text pointing somewhere
 * other than where it says is the shape of a phishing link. Apex is the
 * canonical form, matching og:url in public/index.html. If apex ever stops
 * resolving, fix the DNS rather than pointing the link at a host the text does
 * not name.
 */
export const WATERMARK_PREFIX = 'made with ';
export const WATERMARK_LABEL = 'tallytape.space';
export const WATERMARK_HREF = 'https://tallytape.space';

/**
 * The anonymous invoice counter.
 *
 * One integer, same origin, no body, no identifier. It is never in the
 * critical path: every failure resolves to null and the caller renders
 * nothing. There is no error state on the page, because a number we do not
 * have is a number we do not assert.
 */
const COUNTER_ENDPOINT = '/api/count';

const parseCount = (response) =>
  response.ok
    ? response.json().then((d) => (typeof d.count === 'number' ? d.count : null))
    : null;

export const readInvoiceCount = () =>
  fetch(COUNTER_ENDPOINT).then(parseCount).catch(() => null);

export const bumpInvoiceCount = () =>
  fetch(COUNTER_ENDPOINT, { method: 'POST', keepalive: true })
    .then(parseCount)
    .catch(() => null);

/** "1 invoice made" / "4,182 invoices made", locale pinned like money(). */
export const countLabel = (n) =>
  `${Number(n).toLocaleString('en-US')} ${
    Number(n) === 1 ? 'invoice' : 'invoices'
  } made`;

/**
 * Whether a line item belongs on the artifact at all.
 *
 * isFormValid() only requires *some* item to be complete, so an invoice can
 * carry rows the user added and left blank. Those must not print: the tape
 * would otherwise assert "What did you do?" at 1 x 0.00 on someone's client's
 * invoice. A row with no description and no amount contributes nothing to the
 * total either, so dropping it cannot make the printed lines stop summing to
 * TOTAL. Anything with a non-zero amount always prints, even unlabelled.
 */
export const isPrintableItem = (item) => {
  if (!item) return false;
  const amount = (parseFloat(item.hours) || 0) * (parseFloat(item.rate) || 0);
  return Boolean(String(item.description || '').trim()) || amount !== 0;
};

const qtyLabel = (n) => String(Number(n) || 0);

/**
 * ISO date to receipt format. Parses the string parts rather than going
 * through Date, because new Date('2026-08-15') is parsed as UTC and prints as
 * the day before anywhere west of Greenwich.
 */
export function formatTapeDate(iso) {
  const m = /^(\d{4})-(\d{2})-(\d{2})$/.exec(String(iso || ''));
  return m ? `${m[2]}.${m[3]}.${m[1]}` : String(iso || '');
}

/** Courier advance is 0.6em, so a mono run's width is exactly computable. */
const monoWidthIn = (text, sizePt, charSpaceIn = 0) => {
  const len = String(text).length;
  return len * ((sizePt * 0.6) / 72) + Math.max(0, len - 1) * charSpaceIn;
};

/** The two party blocks, shared by both renderings so neither drifts. */
const partyBlocks = (d) => [
  {
    label: 'FROM',
    name: d.fromName,
    placeholder: "That's you",
    details: [
      d.fromEmail,
      d.fromPhone,
      d.fromAddress,
      d.fromCity,
      d.fromWebsite,
      d.fromTaxId ? `TAX ${d.fromTaxId}` : '',
    ].filter(Boolean),
  },
  {
    label: 'TO',
    name: d.toName,
    placeholder: "Who's paying you?",
    details: [d.toEmail, d.toAddress, d.toCity].filter(Boolean),
  },
];

/** Desaturate on a canvas so the screen and the PDF share identical bytes. */
function toGrayscaleDataUrl(dataUrl) {
  return new Promise((resolve) => {
    const img = new Image();
    img.onload = () => {
      try {
        const canvas = document.createElement('canvas');
        canvas.width = img.naturalWidth;
        canvas.height = img.naturalHeight;
        const ctx = canvas.getContext('2d');
        ctx.drawImage(img, 0, 0);
        const data = ctx.getImageData(0, 0, canvas.width, canvas.height);
        const px = data.data;
        for (let i = 0; i < px.length; i += 4) {
          const l = 0.2126 * px[i] + 0.7152 * px[i + 1] + 0.0722 * px[i + 2];
          px[i] = px[i + 1] = px[i + 2] = l;
        }
        ctx.putImageData(data, 0, 0);
        resolve(canvas.toDataURL('image/png'));
      } catch (e) {
        resolve(dataUrl);
      }
    };
    img.onerror = () => resolve(dataUrl);
    img.src = dataUrl;
  });
}

export default function TallyTape() {
  const generateInvoiceNumber = () => {
    const year = new Date().getFullYear();
    const random = Math.floor(Math.random() * 9999)
      .toString()
      .padStart(4, '0');
    return `${year}-${random}`;
  };

  const loadSavedData = () => {
    const blank = {
      fromName: '',
      fromEmail: '',
      fromPhone: '',
      fromAddress: '',
      fromCity: '',
      fromWebsite: '',
      fromTaxId: '',
      toName: '',
      toEmail: '',
      toAddress: '',
      toCity: '',
      invoiceNumber: generateInvoiceNumber(),
      invoiceDate: new Date().toISOString().split('T')[0],
      notes: '',
      items: [{ description: '', hours: 1, rate: 0 }],
      logo: null,
      showWatermark: true,
      currency: '$',
    };
    try {
      const saved = localStorage.getItem('tallytape_data');
      if (saved) {
        const parsed = JSON.parse(saved);
        return {
          ...blank,
          ...parsed,
          invoiceNumber: generateInvoiceNumber(),
          invoiceDate: new Date().toISOString().split('T')[0],
          items: parsed.items || blank.items,
          // Never persisted: base64 images blow past the localStorage quota.
          logo: null,
          showWatermark: parsed.showWatermark !== false,
          currency:
            typeof parsed.currency === 'string' ? parsed.currency : '$',
        };
      }
    } catch (e) {
      console.error('Failed to load saved data', e);
    }
    return blank;
  };

  const [formData, setFormData] = useState(loadSavedData);
  const [justSaved, setJustSaved] = useState(false);
  const [showFromDetails, setShowFromDetails] = useState(false);
  const [showToDetails, setShowToDetails] = useState(false);
  const [showNotes, setShowNotes] = useState(false);
  const [showLogo, setShowLogo] = useState(false);
  const [customCurrency, setCustomCurrency] = useState(false);
  /** null means "we do not have a count", which renders no line at all. */
  const [invoiceCount, setInvoiceCount] = useState(null);
  /** Bumped on generate to replay the unspool. 0 means "never generated". */
  const [unspool, setUnspool] = useState(0);

  useEffect(() => {
    if (
      formData.fromAddress ||
      formData.fromCity ||
      formData.fromWebsite ||
      formData.fromTaxId
    ) {
      setShowFromDetails(true);
    }
    if (formData.toAddress || formData.toCity) setShowToDetails(true);
    if (formData.notes) setShowNotes(true);
    if (!CURRENCIES.some((c) => c.symbol === formData.currency)) {
      setCustomCurrency(true);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    let live = true;
    readInvoiceCount().then((n) => {
      if (live && n !== null) setInvoiceCount(n);
    });
    return () => {
      live = false;
    };
  }, []);

  useEffect(() => {
    const timer = setTimeout(() => {
      try {
        const { logo, ...persistable } = formData;
        localStorage.setItem('tallytape_data', JSON.stringify(persistable));
      } catch (e) {
        console.error('Failed to save data', e);
      }
    }, 500);
    return () => clearTimeout(timer);
  }, [formData]);

  const updateField = (field, value) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
  };

  const updateItem = (index, field, value) => {
    setFormData((prev) => {
      const items = [...prev.items];
      items[index] = { ...items[index], [field]: value };
      return { ...prev, items };
    });
  };

  const addItem = () => {
    setFormData((prev) => ({
      ...prev,
      items: [...prev.items, { description: '', hours: 1, rate: 0 }],
    }));
  };

  const removeItem = (index) => {
    setFormData((prev) =>
      prev.items.length > 1
        ? { ...prev, items: prev.items.filter((_, i) => i !== index) }
        : prev
    );
  };

  const handleLogoUpload = (e) => {
    const file = e.target.files[0];
    if (!file || !file.type.startsWith('image/')) return;
    const reader = new FileReader();
    reader.onload = async (event) => {
      // Converted once, here, so the tape and the PDF embed the same pixels
      // rather than the screen applying a CSS filter the PDF cannot.
      const gray = await toGrayscaleDataUrl(event.target.result);
      setFormData((prev) => ({ ...prev, logo: gray }));
    };
    reader.readAsDataURL(file);
  };

  const removeLogo = () => {
    setFormData((prev) => ({ ...prev, logo: null }));
    setShowLogo(false);
  };

  const calculateTotal = useCallback(
    () =>
      formData.items.reduce(
        (sum, item) =>
          sum + (parseFloat(item.hours) || 0) * (parseFloat(item.rate) || 0),
        0
      ),
    [formData.items]
  );

  const isFormValid = () => {
    if (
      !formData.fromName.trim() ||
      !formData.toName.trim() ||
      !formData.invoiceNumber.trim()
    ) {
      return false;
    }
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (formData.fromEmail && !emailRegex.test(formData.fromEmail.trim())) {
      return false;
    }
    if (formData.toEmail && !emailRegex.test(formData.toEmail.trim())) {
      return false;
    }
    return formData.items.some(
      (item) =>
        item.description.trim() &&
        parseFloat(item.hours) > 0 &&
        parseFloat(item.rate) > 0
    );
  };

  /* =========================================================================
     PDF. The same tape, drawn with jsPDF primitives, centred on US Letter.

     Letter is deliberate: clients print invoices and forward them to
     bookkeepers who expect a standard page. The white space either side of
     the tape is part of the effect, not wasted margin.
     ========================================================================= */
  const generatePDF = () => {
    const doc = new jsPDF({ unit: 'in', format: 'letter' });
    const data = formData;
    const total = calculateTotal();

    /* jsPDF draw state is global and sticky, so every helper below sets
       everything it depends on and resets the dash pattern when it leaves. */
    const setInk = (rgb) => doc.setTextColor(rgb[0], rgb[1], rgb[2]);
    const strokeStyle = (rgb, width) => {
      doc.setDrawColor(rgb[0], rgb[1], rgb[2]);
      doc.setLineWidth(width);
      doc.setLineDashPattern([], 0);
    };

    const text = (str, x, y, o = {}) => {
      doc.setFont('courier', o.bold ? 'bold' : 'normal');
      doc.setFontSize(o.size || PT(TS.body));
      setInk(o.color || INK);
      doc.setCharSpace(o.track || 0);
      doc.text(String(str), x, y, o.align ? { align: o.align } : undefined);
      doc.setCharSpace(0);
    };

    /** Tracked labels are centred by hand: align:'center' ignores charSpace. */
    const centeredText = (str, y, o = {}) => {
      const size = o.size || PT(TS.body);
      const w = monoWidthIn(str, size, o.track || 0);
      text(str, TAPE_X + PDF_TAPE_W / 2 - w / 2, y, { ...o, size });
    };

    /**
     * A tear edge. Semicircles bitten out of the paper, matching the
     * radial-gradient mask on screen: the top edge dips down into the paper,
     * the bottom edge dips up. Cubic control points at 4r/3 approximate a
     * semicircle closely enough at this size.
     */
    const drawTearEdge = (y, direction) => {
      const k = (4 / 3) * PERF_R;
      const dir = direction === 'down' ? 1 : -1;
      const count = Math.floor(PDF_TAPE_W / PERF_PITCH);
      const inset =
        (PDF_TAPE_W - count * PERF_PITCH) / 2 + (PERF_PITCH - 2 * PERF_R) / 2;
      strokeStyle(RULE, 0.008);
      let cursor = TAPE_X;
      for (let i = 0; i < count; i++) {
        const sx = TAPE_X + inset + i * PERF_PITCH;
        if (sx > cursor) doc.line(cursor, y, sx, y);
        doc.lines(
          [[0, dir * k, 2 * PERF_R, dir * k, 2 * PERF_R, 0]],
          sx,
          y,
          [1, 1],
          'S',
          false
        );
        cursor = sx + 2 * PERF_R;
      }
      if (cursor < TAPE_X + PDF_TAPE_W) {
        doc.line(cursor, y, TAPE_X + PDF_TAPE_W, y);
      }
    };

    const drawRule = (y) => {
      doc.setDrawColor(RULE[0], RULE[1], RULE[2]);
      doc.setLineWidth(0.007);
      doc.setLineDashPattern([3 * PX, 4 * PX], 0);
      doc.line(CONTENT_X, y, CONTENT_R, y);
      // Always reset: a live dash pattern would otherwise turn the next
      // tally marks and tear edges dashed too.
      doc.setLineDashPattern([], 0);
    };

    /** Matches the screen rule's `margin: 18px 0`. */
    const ruleBlock = () => {
      yPos += 8 * PX;
      drawRule(yPos);
      yPos += 26 * PX;
    };

    /** Tally marks, drawn as short lines. Returns the width consumed. */
    const drawTally = (count, x, baseline, g) => {
      const marks = tallyMarks(count, g);
      if (!marks.segments.length) return 0;
      strokeStyle(TERRACOTTA, g.width);
      doc.setLineCap('round');
      const top = baseline - g.h;
      marks.segments.forEach(([x1, y1, x2, y2]) => {
        doc.line(x + x1, top + y1, x + x2, top + y2);
      });
      doc.setLineCap('butt');
      return marks.width;
    };

    let yPos = TAPE_TOP;

    const startPage = () => {
      yPos = TAPE_TOP;
      drawTearEdge(yPos, 'down');
      yPos += 30 * PX;
    };

    const endPage = (bottomY) => {
      drawTearEdge(bottomY, 'up');
      strokeStyle(RULE, 0.006);
      doc.line(TAPE_X, TAPE_TOP, TAPE_X, bottomY);
      doc.line(TAPE_X + PDF_TAPE_W, TAPE_TOP, TAPE_X + PDF_TAPE_W, bottomY);
    };

    /** The old generatePDF had no pagination at all and silently ran off the
        page. A tape is narrow, so overflow is likelier, not less likely. */
    const ensureSpace = (needed) => {
      if (yPos + needed <= TAPE_BOTTOM - 0.12) return;
      endPage(TAPE_BOTTOM);
      doc.addPage();
      startPage();
    };

    startPage();

    /* --- Wordmark lockup -------------------------------------------------- */
    if (data.logo) {
      try {
        const props = doc.getImageProperties(data.logo);
        const maxH = 34 * PX;
        const maxW = 150 * PX;
        let w = maxW;
        let h = (props.height / props.width) * maxW;
        if (h > maxH) {
          h = maxH;
          w = (props.width / props.height) * maxH;
        }
        ensureSpace(h + 0.14);
        doc.addImage(
          data.logo,
          'PNG',
          TAPE_X + (PDF_TAPE_W - w) / 2,
          yPos,
          w,
          h
        );
        yPos += h + 14 * PX;
      } catch (e) {
        console.error('Failed to add logo', e);
      }
    }

    const lg = TALLY.pdfLg;
    const markW = tallyMarks(5, lg).width;
    yPos += lg.h;
    drawTally(5, TAPE_X + PDF_TAPE_W / 2 - markW / 2, yPos, lg);
    yPos += 18 * PX;

    centeredText('TALLYTAPE', yPos, {
      size: PT(TS.word),
      track: 4.1 * PX,
      bold: true,
    });
    yPos += 19 * PX;

    // Kept small, but kept: an AP inbox searches PDFs for the word "invoice".
    centeredText('INVOICE', yPos, {
      size: PT(TS.kicker),
      track: 2.7 * PX,
      color: INK_FAINT,
    });
    yPos += 39 * PX;

    /* --- Meta ------------------------------------------------------------- */
    const metaRow = (label, value) => {
      text(label, CONTENT_X, yPos, {
        size: PT(TS.label),
        track: 1.3 * PX,
        color: INK_FAINT,
      });
      text(value, CONTENT_R, yPos, { size: PT(TS.body), align: 'right' });
      yPos += 21 * PX;
    };
    metaRow('NO.', data.invoiceNumber || '--');
    metaRow('DATE', formatTapeDate(data.invoiceDate));

    ruleBlock();

    /* --- From / To -------------------------------------------------------- */
    partyBlocks(data).forEach((party, i) => {
      const nameLines = wrapMono(party.name || party.placeholder);
      const detailLines = party.details.flatMap((d) => wrapMono(d));
      ensureSpace(
        (21 + nameLines.length * 17 + detailLines.length * 17 + 12) * PX
      );

      text(party.label, CONTENT_X, yPos, {
        size: PT(TS.label),
        track: 1.3 * PX,
        color: INK_FAINT,
      });
      yPos += 21 * PX;

      nameLines.forEach((line) => {
        text(line, CONTENT_X, yPos, {
          size: PT(TS.body),
          color: party.name ? INK : INK_FAINT,
        });
        yPos += 17 * PX;
      });
      detailLines.forEach((line) => {
        text(line, CONTENT_X, yPos, {
          size: PT(TS.small),
          color: INK_SOFT,
        });
        yPos += 17 * PX;
      });
      yPos += (i === 0 ? 12 : 2) * PX;
    });

    ruleBlock();

    /* --- Line items ------------------------------------------------------- */
    // Rows the user added and left blank never reach the artifact.
    const printableItems = data.items.filter(isPrintableItem);
    printableItems.forEach((item, itemIndex) => {
      const isLast = itemIndex === printableItems.length - 1;
      const qty = parseFloat(item.hours) || 0;
      const rate = parseFloat(item.rate) || 0;
      const amount = qty * rate;
      const descLines = wrapMono(item.description);
      ensureSpace((descLines.length * 16 + 8 + 30) * PX);

      descLines.forEach((line) => {
        text(line, CONTENT_X, yPos, { size: PT(TS.body), color: INK });
        yPos += 16 * PX;
      });

      yPos += 8 * PX;
      let cursor = CONTENT_X;
      if (showsTally(qty)) {
        cursor += drawTally(qty, cursor, yPos, TALLY.pdfSm) + 8 * PX;
      }
      text(`${qtyLabel(qty)} x ${money(rate, data.currency)}`, cursor, yPos, {
        size: PT(TS.small),
        color: INK_SOFT,
      });
      text(money(amount, data.currency), CONTENT_R, yPos, {
        size: PT(TS.body),
        align: 'right',
        bold: true,
      });
      // The between-items gap reserves room for the next description's
      // ascent. After the last row there is no description, only the rule.
      yPos += (isLast ? 19 : 30) * PX;
    });

    ruleBlock();

    /* --- Total ------------------------------------------------------------ */
    ensureSpace(40 * PX);
    text('TOTAL', CONTENT_X, yPos, {
      size: PT(TS.label),
      track: 1.3 * PX,
      color: INK_FAINT,
    });
    text(money(total, data.currency), CONTENT_R, yPos, {
      size: PT(TS.total),
      align: 'right',
      bold: true,
    });
    yPos += 30 * PX;

    /* --- Footer ----------------------------------------------------------- */
    if (data.notes) {
      const noteLines = wrapMono(data.notes);
      ensureSpace((noteLines.length * 14 + 10) * PX);
      noteLines.forEach((line) => {
        const w = monoWidthIn(line, PT(TS.small));
        text(line, TAPE_X + PDF_TAPE_W / 2 - w / 2, yPos, {
          size: PT(TS.small),
          color: INK_SOFT,
        });
        yPos += 14 * PX;
      });
      yPos += 6 * PX;
    }

    // No THANK YOU, no terms, no due date. The artifact says nothing in the
    // sender's voice that the sender did not type. The note field carries all
    // of it, and prints only when it has something in it.
    if (data.showWatermark) {
      ensureSpace(24 * PX);
      const wmSize = PT(TS.fine);
      const wmLine = WATERMARK_PREFIX + WATERMARK_LABEL;
      const wmX = TAPE_X + PDF_TAPE_W / 2 - monoWidthIn(wmLine, wmSize) / 2;
      doc.setFont('courier', 'normal');
      doc.setFontSize(wmSize);
      setInk(INK_FAINT);
      // textWithLink sizes its annotation from the current font, and
      // getTextWidth ignores charSpace, so clear any tracking first.
      doc.setCharSpace(0);
      doc.text(WATERMARK_PREFIX, wmX, yPos);
      doc.textWithLink(
        WATERMARK_LABEL,
        wmX + monoWidthIn(WATERMARK_PREFIX, wmSize),
        yPos,
        { url: WATERMARK_HREF }
      );
      yPos += 14 * PX;
    }

    endPage(Math.min(yPos + 12 * PX, TAPE_BOTTOM));

    doc.save(`invoice-${data.invoiceNumber || 'draft'}.pdf`);

    /*
     * Counted here, after save() has returned without throwing, because that
     * is the last observable moment: handing the blob to the browser is not
     * the same as the file reaching disk, and nothing reports the latter. Fire
     * and forget, so a blocked or failed request cannot affect the invoice
     * that has already been generated.
     */
    bumpInvoiceCount().then((n) => {
      if (n !== null) setInvoiceCount(n);
    });

    setUnspool((n) => n + 1);
    setJustSaved(true);
    setTimeout(() => setJustSaved(false), 2600);
  };

  useEffect(() => {
    const handleKeyboard = (e) => {
      if ((e.metaKey || e.ctrlKey) && e.key === 's') {
        e.preventDefault();
        if (isFormValid()) generatePDF();
      }
      if ((e.metaKey || e.ctrlKey) && e.key === 'k') {
        e.preventDefault();
        addItem();
      }
    };
    window.addEventListener('keydown', handleKeyboard);
    return () => window.removeEventListener('keydown', handleKeyboard);
  }, [formData]); // eslint-disable-line react-hooks/exhaustive-deps

  const total = calculateTotal();
  const valid = isFormValid();

  /* =========================================================================
     Screen rendering. Plain functions rather than components, so this file
     still exports exactly one component.
     ========================================================================= */

  const tallySvg = (count, g, opacity = 1) => {
    const marks = tallyMarks(count, g);
    if (!marks.segments.length) return null;
    const pad = g.width;
    return (
      <svg
        width={marks.width + pad}
        height={g.h + pad}
        viewBox={`0 0 ${marks.width + pad} ${g.h + pad}`}
        aria-hidden="true"
        style={{ display: 'block', flex: '0 0 auto', opacity }}
      >
        <g
          stroke="var(--accent-fixed)"
          strokeWidth={g.width}
          strokeLinecap="round"
        >
          {marks.segments.map(([x1, y1, x2, y2], i) => (
            <line
              key={i}
              x1={x1 + pad / 2}
              y1={y1 + pad / 2}
              x2={x2 + pad / 2}
              y2={y2 + pad / 2}
            />
          ))}
        </g>
      </svg>
    );
  };

  const mono = (size, color, extra) => ({
    fontFamily: 'var(--font-mono)',
    fontSize: `${size}px`,
    color,
    lineHeight: 1.3,
    ...extra,
  });

  const tapeRule = (key) => (
    <div
      key={key}
      style={{
        height: '1px',
        margin: '18px 0',
        backgroundImage:
          'repeating-linear-gradient(90deg, var(--rule) 0 3px, transparent 3px 7px)',
      }}
    />
  );

  /* CSS letter-spacing adds a trailing gap after the last glyph, which pulls
     centred text half a step left. textIndent of half the track puts the
     optical centre back where the PDF's hand-computed centring puts it. */
  const trackedLabel = (str, track) =>
    mono(TS.label, 'var(--ink-faint)', {
      letterSpacing: `${track}px`,
      textIndent: `${track / 2}px`,
    });

  const printableItems = formData.items.filter(isPrintableItem);

  const renderTape = () => (
    <div
      style={{
        filter: 'drop-shadow(0 3px 14px rgba(40, 34, 28, 0.14))',
        width: `${SCREEN_TAPE_W}px`,
      }}
    >
      <div className="tape-anim" key={unspool}>
        <div
          className="perf-paper"
          style={{ padding: '30px 26px 32px', width: '100%' }}
        >
          {/* --- Wordmark lockup --- */}
          {formData.logo && (
            <div style={{ textAlign: 'center', marginBottom: '14px' }}>
              <img
                src={formData.logo}
                alt=""
                style={{
                  height: '34px',
                  width: 'auto',
                  maxWidth: '150px',
                  objectFit: 'contain',
                  display: 'inline-block',
                }}
              />
            </div>
          )}
          <div style={{ textAlign: 'center' }}>
            <div style={{ display: 'flex', justifyContent: 'center' }}>
              {tallySvg(5, TALLY.screenLg)}
            </div>
            <div
              style={mono(TS.word, 'var(--ink)', {
                letterSpacing: '4.1px',
                textIndent: '2.05px',
                fontWeight: 700,
                marginTop: '10px',
              })}
            >
              TALLYTAPE
            </div>
            <div
              style={mono(TS.kicker, 'var(--ink-faint)', {
                letterSpacing: '2.7px',
                textIndent: '1.35px',
                marginTop: '5px',
              })}
            >
              INVOICE
            </div>
          </div>

          {/* --- Meta --- */}
          <div style={{ marginTop: '24px' }}>
            {[
              ['NO.', formData.invoiceNumber || '--'],
              ['DATE', formatTapeDate(formData.invoiceDate)],
            ].map(([label, value]) => (
              <div
                key={label}
                style={{
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'baseline',
                  marginBottom: '4px',
                }}
              >
                <span style={trackedLabel(label, 1.3)}>{label}</span>
                <span style={mono(TS.body, 'var(--ink)')}>{value}</span>
              </div>
            ))}
          </div>

          {tapeRule('r1')}

          {/* --- From / To --- */}
          {partyBlocks(formData).map((party) => (
            <div key={party.label} style={{ marginBottom: '14px' }}>
              <div
                style={{ ...trackedLabel(party.label, 1.3), marginBottom: '5px' }}
              >
                {party.label}
              </div>
              {wrapMono(party.name || party.placeholder).map((line, i) => (
                <div
                  key={i}
                  style={mono(
                    TS.body,
                    party.name ? 'var(--ink)' : 'var(--ink-faint)',
                    { whiteSpace: 'pre' }
                  )}
                >
                  {line}
                </div>
              ))}
              {party.details
                .flatMap((d) => wrapMono(d))
                .map((line, i) => (
                  <div
                    key={i}
                    style={mono(TS.small, 'var(--ink-soft)', {
                      whiteSpace: 'pre',
                      marginTop: '2px',
                    })}
                  >
                    {line}
                  </div>
                ))}
            </div>
          ))}

          {tapeRule('r2')}

          {/* --- Line items --- */}
          {printableItems.length === 0 ? (
            /* Empty-state hint only. The PDF cannot be generated in this
               state, so the two renderings never actually disagree. */
            <div style={mono(TS.body, 'var(--ink-faint)')}>What did you do?</div>
          ) : (
            printableItems.map((item, index) => {
              const qty = parseFloat(item.hours) || 0;
              const rate = parseFloat(item.rate) || 0;
              return (
                <div key={index} style={{ marginBottom: '14px' }}>
                  {wrapMono(item.description).map((line, i) => (
                    <div
                      key={i}
                      style={mono(TS.body, 'var(--ink)', {
                        whiteSpace: 'pre',
                      })}
                    >
                      {line}
                    </div>
                  ))}
                  <div
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: '8px',
                      marginTop: '6px',
                    }}
                  >
                    {showsTally(qty) && tallySvg(qty, TALLY.screenSm)}
                    <span style={mono(TS.small, 'var(--ink-soft)')}>
                      {qtyLabel(qty)} x {money(rate, formData.currency)}
                    </span>
                    <span
                      style={mono(TS.body, 'var(--ink)', {
                        marginLeft: 'auto',
                        fontWeight: 700,
                      })}
                    >
                      {money(qty * rate, formData.currency)}
                    </span>
                  </div>
                </div>
              );
            })
          )}

          {tapeRule('r3')}

          {/* --- Total --- */}
          <div
            style={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'baseline',
            }}
          >
            <span style={trackedLabel('TOTAL', 1.3)}>TOTAL</span>
            <span
              style={mono(TS.total, 'var(--ink)', { fontWeight: 700 })}
            >
              {money(total, formData.currency)}
            </span>
          </div>

          {/* --- Footer --- */}
          <div style={{ marginTop: '22px', textAlign: 'center' }}>
            {formData.notes &&
              wrapMono(formData.notes).map((line, i) => (
                <div
                  key={i}
                  style={mono(TS.small, 'var(--ink-soft)', {
                    whiteSpace: 'pre',
                  })}
                >
                  {line}
                </div>
              ))}
            {/* The watermark toggle lives on the artifact, not in the form.
                You see the line, you click it, it is gone. Claiming a
                watermark is optional and then burying it in a checkbox is the
                thing this tool exists not to do. */}
            {formData.showWatermark ? (
              <div
                className="tape-mark"
                style={{ marginTop: formData.notes ? '12px' : 0 }}
              >
                <span style={mono(TS.fine, 'var(--ink-faint)')}>
                  {WATERMARK_PREFIX}
                  <a
                    href={WATERMARK_HREF}
                    target="_blank"
                    rel="noopener noreferrer"
                    style={{ color: 'inherit', textDecoration: 'none' }}
                  >
                    {WATERMARK_LABEL}
                  </a>
                </span>
                <button
                  type="button"
                  className="tape-mark-x"
                  onClick={() => updateField('showWatermark', false)}
                  title="Remove this line"
                  aria-label="Remove the tallytape.space line"
                >
                  ×
                </button>
              </div>
            ) : (
              <button
                type="button"
                className="tape-mark-restore"
                onClick={() => updateField('showWatermark', true)}
                style={mono(TS.fine, 'var(--ink-faint)')}
              >
                {`+ ${WATERMARK_PREFIX}${WATERMARK_LABEL}`}
              </button>
            )}
          </div>
        </div>
      </div>
    </div>
  );

  /* --- Form pieces --------------------------------------------------------- */

  const inputStyle = (extra) => ({
    width: '100%',
    padding: '10px 12px',
    fontSize: '14px',
    border: '1px solid var(--rule-light)',
    borderRadius: '4px',
    background: 'var(--paper-shade)',
    color: 'var(--ink)',
    fontFamily: 'var(--font-sans)',
    ...extra,
  });

  const monoInput = (extra) =>
    inputStyle({ fontFamily: 'var(--font-mono)', fontWeight: 500, ...extra });

  const sectionLabel = {
    fontSize: '11px',
    fontWeight: 600,
    color: 'var(--ink-faint)',
    marginBottom: '12px',
    textTransform: 'uppercase',
    letterSpacing: '0.12em',
    fontFamily: 'var(--font-mono)',
  };

  const fieldLabel = {
    display: 'block',
    fontSize: '10px',
    color: 'var(--ink-faint)',
    marginBottom: '6px',
    textTransform: 'uppercase',
    fontWeight: 600,
    letterSpacing: '0.1em',
    fontFamily: 'var(--font-mono)',
  };

  const quietButton = {
    padding: '6px 0',
    background: 'transparent',
    color: 'var(--ink-faint)',
    border: 'none',
    cursor: 'pointer',
    fontSize: '13px',
    fontWeight: 500,
  };

  const formDivider = (
    <div
      style={{
        height: '1px',
        margin: '28px 0',
        backgroundImage:
          'repeating-linear-gradient(90deg, var(--rule) 0 3px, transparent 3px 7px)',
      }}
    />
  );

  return (
    <div
      className="tt"
      style={{
        minHeight: '100vh',
        background: 'var(--bg)',
        padding: '48px 20px 64px',
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
      }}
    >
      {/* ---------------- Header ---------------- */}
      <header
        style={{
          width: '100%',
          maxWidth: '760px',
          marginBottom: '48px',
          textAlign: 'center',
        }}
      >
        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            gap: '12px',
            marginBottom: '20px',
          }}
        >
          {/* The hand-drawn mark stays the site's logo. The tape uses the
              crisp geometric marks, which the PDF can reproduce exactly. */}
          <svg width="40" height="40" viewBox="0 0 40 40" aria-hidden="true">
            <g
              stroke="var(--accent)"
              strokeWidth="2.5"
              strokeLinecap="round"
              fill="none"
            >
              <path d="M8.2 10.5 Q8.5 15 8.3 20 Q8.6 25 9 29.8" />
              <path d="M14.3 10 Q14.8 15 14.5 20 Q14.2 25 15.2 30" />
              <path d="M20.5 10.2 Q20.2 15 20.8 20 Q20.3 25 21 30.2" />
              <path d="M26.2 10.5 Q26.8 15 26.5 20 Q26.2 25 27.3 29.8" />
              <path
                d="M5.5 12.5 Q12 16 17 19.5 Q22 23 29.5 27.8"
                strokeWidth="3"
              />
            </g>
          </svg>
          <h1
            style={{
              fontSize: '40px',
              fontWeight: 600,
              margin: 0,
              color: 'var(--text)',
              letterSpacing: '-0.02em',
            }}
          >
            TallyTape
          </h1>
        </div>

        {/* The reason this tool gets posted anywhere. Second-largest text on
            the page, directly beneath the title. Wording is untouched. */}
        <p
          style={{
            fontSize: '22px',
            lineHeight: 1.45,
            color: 'var(--text)',
            margin: '0 auto 14px',
            maxWidth: '620px',
            fontWeight: 500,
            letterSpacing: '-0.01em',
          }}
        >
          Your invoice data never leaves your browser.{' '}
          {/* The punchline should never be split across a line break. */}
          <span style={{ whiteSpace: 'nowrap' }}>Not even once.</span>
        </p>

        {/* The running tally. A tool called TallyTape counting in tally marks
            is the product explaining itself. Renders only when we actually
            have a number: no zero, no spinner, no placeholder. */}
        {invoiceCount !== null && (
          <div
            style={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              gap: '9px',
              marginBottom: '14px',
            }}
          >
            {tallySvg(5, TALLY.screenSm)}
            <span style={mono(TS.small, 'var(--ink-faint)')}>
              {countLabel(invoiceCount)}
            </span>
          </div>
        )}
        <p
          style={{
            fontSize: '15px',
            color: 'var(--text-secondary)',
            margin: 0,
          }}
        >
          The invoice generator that doesn't waste your time.
        </p>
      </header>

      {/* ---------------- Form + tape ---------------- */}
      <div
        className="tt-columns"
        style={{
          display: 'flex',
          gap: '44px',
          alignItems: 'flex-start',
          justifyContent: 'center',
          width: '100%',
          maxWidth: '1040px',
        }}
      >
        {/* ---- Form ---- */}
        <div
          className="perf-paper tt-form"
          style={{
            flex: '1 1 560px',
            maxWidth: '600px',
            padding: '40px 40px 44px',
            position: 'relative',
          }}
        >
          {/* Logo control */}
          <div
            style={{
              position: 'absolute',
              top: '26px',
              right: '26px',
              display: 'flex',
              alignItems: 'center',
              gap: '8px',
            }}
          >
            {formData.logo ? (
              <>
                <img
                  src={formData.logo}
                  alt="Your logo"
                  style={{
                    height: '40px',
                    width: 'auto',
                    maxWidth: '110px',
                    objectFit: 'contain',
                  }}
                />
                <button
                  onClick={removeLogo}
                  style={{
                    padding: '3px 7px',
                    background: 'transparent',
                    color: 'var(--ink-faint)',
                    border: '1px solid var(--rule-light)',
                    borderRadius: '4px',
                    cursor: 'pointer',
                    fontSize: '13px',
                    lineHeight: 1,
                  }}
                  aria-label="Remove logo"
                >
                  ×
                </button>
              </>
            ) : showLogo ? (
              <label
                style={{
                  padding: '6px 12px',
                  background: 'var(--paper-shade)',
                  color: 'var(--accent-fixed)',
                  border: '1px solid var(--accent-fixed)',
                  borderRadius: '4px',
                  cursor: 'pointer',
                  fontSize: '13px',
                  fontWeight: 500,
                }}
              >
                Choose file
                <input
                  type="file"
                  accept="image/*"
                  onChange={handleLogoUpload}
                  style={{ display: 'none' }}
                />
              </label>
            ) : (
              <button
                onClick={() => setShowLogo(true)}
                style={{
                  padding: '6px 12px',
                  background: 'transparent',
                  color: 'var(--ink-faint)',
                  border: '1px solid var(--rule-light)',
                  borderRadius: '4px',
                  cursor: 'pointer',
                  fontSize: '13px',
                  fontWeight: 500,
                }}
              >
                + Logo?
              </button>
            )}
          </div>

          {/* Invoice meta */}
          <div
            className="tt-meta"
            style={{
              display: 'grid',
              gridTemplateColumns: '1.2fr 1fr 0.62fr',
              gap: '16px',
              marginTop: '52px',
            }}
          >
            <div>
              <label style={fieldLabel}>Invoice Number</label>
              <input
                type="text"
                value={formData.invoiceNumber}
                onChange={(e) => updateField('invoiceNumber', e.target.value)}
                placeholder="2026-0001"
                style={monoInput()}
              />
            </div>
            <div>
              <label style={fieldLabel}>Date</label>
              <input
                type="date"
                value={formData.invoiceDate}
                onChange={(e) => updateField('invoiceDate', e.target.value)}
                style={monoInput()}
              />
            </div>
            <div>
              <label style={fieldLabel}>Currency</label>
              {customCurrency ? (
                <input
                  type="text"
                  value={formData.currency}
                  onChange={(e) => updateField('currency', e.target.value)}
                  onBlur={() => {
                    if (!formData.currency.trim()) {
                      updateField('currency', '$');
                      setCustomCurrency(false);
                    }
                  }}
                  maxLength={4}
                  placeholder="R$"
                  aria-label="Currency symbol"
                  style={monoInput({ textAlign: 'center' })}
                />
              ) : (
                <select
                  value={formData.currency}
                  onChange={(e) => {
                    if (e.target.value === '__other') setCustomCurrency(true);
                    else updateField('currency', e.target.value);
                  }}
                  aria-label="Currency"
                  style={monoInput({ cursor: 'pointer' })}
                >
                  {CURRENCIES.map((c) => (
                    <option key={c.code} value={c.symbol}>
                      {c.symbol} {c.code}
                    </option>
                  ))}
                  <option value="__other">Other</option>
                </select>
              )}
            </div>
          </div>

          {formDivider}

          {/* From */}
          <div>
            <h2 style={sectionLabel}>From</h2>
            <input
              type="text"
              value={formData.fromName}
              onChange={(e) => updateField('fromName', e.target.value)}
              placeholder="That's you *"
              style={inputStyle({ fontSize: '15px', marginBottom: '12px' })}
            />
            <div
              style={{
                display: 'grid',
                gridTemplateColumns: '1fr 1fr',
                gap: '12px',
                marginBottom: '4px',
              }}
            >
              <input
                type="email"
                value={formData.fromEmail}
                onChange={(e) => updateField('fromEmail', e.target.value)}
                placeholder="you@domain.com"
                style={inputStyle()}
              />
              <input
                type="tel"
                value={formData.fromPhone}
                onChange={(e) => updateField('fromPhone', e.target.value)}
                placeholder="Optional"
                style={inputStyle()}
              />
            </div>
            <button
              onClick={() => setShowFromDetails(!showFromDetails)}
              style={{
                ...quietButton,
                marginBottom: showFromDetails ? '12px' : 0,
              }}
            >
              {showFromDetails
                ? "− That's enough"
                : '+ Need to look more professional?'}
            </button>
            {showFromDetails && (
              <div
                style={{
                  display: 'flex',
                  flexDirection: 'column',
                  gap: '12px',
                  overflow: 'hidden',
                  animation: 'slideDown 200ms ease-out',
                }}
              >
                <input
                  type="text"
                  value={formData.fromAddress}
                  onChange={(e) => updateField('fromAddress', e.target.value)}
                  placeholder="Address"
                  style={inputStyle()}
                />
                <input
                  type="text"
                  value={formData.fromCity}
                  onChange={(e) => updateField('fromCity', e.target.value)}
                  placeholder="City, State ZIP"
                  style={inputStyle()}
                />
                <div
                  style={{
                    display: 'grid',
                    gridTemplateColumns: '1fr 1fr',
                    gap: '12px',
                  }}
                >
                  <input
                    type="text"
                    value={formData.fromWebsite}
                    onChange={(e) => updateField('fromWebsite', e.target.value)}
                    placeholder="website.com"
                    style={inputStyle()}
                  />
                  <input
                    type="text"
                    value={formData.fromTaxId}
                    onChange={(e) => updateField('fromTaxId', e.target.value)}
                    placeholder="Tax ID"
                    style={inputStyle()}
                  />
                </div>
              </div>
            )}
          </div>

          {formDivider}

          {/* To */}
          <div>
            <h2 style={sectionLabel}>To</h2>
            <input
              type="text"
              value={formData.toName}
              onChange={(e) => updateField('toName', e.target.value)}
              placeholder="Who's paying you? *"
              style={inputStyle({ fontSize: '15px', marginBottom: '12px' })}
            />
            <input
              type="email"
              value={formData.toEmail}
              onChange={(e) => updateField('toEmail', e.target.value)}
              placeholder="their@email.com"
              style={inputStyle({ marginBottom: '4px' })}
            />
            <button
              onClick={() => setShowToDetails(!showToDetails)}
              style={{
                ...quietButton,
                marginBottom: showToDetails ? '12px' : 0,
              }}
            >
              {showToDetails ? '− Good enough' : '+ Add their address'}
            </button>
            {showToDetails && (
              <div
                style={{
                  display: 'flex',
                  flexDirection: 'column',
                  gap: '12px',
                  overflow: 'hidden',
                  animation: 'slideDown 200ms ease-out',
                }}
              >
                <input
                  type="text"
                  value={formData.toAddress}
                  onChange={(e) => updateField('toAddress', e.target.value)}
                  placeholder="Address"
                  style={inputStyle()}
                />
                <input
                  type="text"
                  value={formData.toCity}
                  onChange={(e) => updateField('toCity', e.target.value)}
                  placeholder="City, State ZIP"
                  style={inputStyle()}
                />
              </div>
            )}
          </div>

          {formDivider}

          {/* Line items */}
          <div>
            <h2 style={sectionLabel}>Line Items</h2>
            {formData.items.map((item, index) => {
              const qty = parseFloat(item.hours) || 0;
              return (
                <div
                  key={index}
                  style={{
                    // Extra bottom padding reserves room for the tally marks
                    // hanging below the Qty field.
                    padding: '16px 16px 30px',
                    marginBottom: '12px',
                    background: 'var(--paper-shade)',
                    border: '1px solid var(--rule-light)',
                    borderRadius: '4px',
                  }}
                >
                  <input
                    type="text"
                    value={item.description}
                    onChange={(e) =>
                      updateItem(index, 'description', e.target.value)
                    }
                    placeholder="What did you do? *"
                    style={inputStyle({
                      background: 'var(--paper)',
                      marginBottom: '12px',
                    })}
                  />
                  <div
                    style={{
                      display: 'grid',
                      gridTemplateColumns: '1fr 1fr 1.2fr auto',
                      gap: '12px',
                      alignItems: 'end',
                    }}
                  >
                    <div style={{ position: 'relative' }}>
                      <label style={fieldLabel}>Qty</label>
                      <input
                        type="number"
                        value={item.hours}
                        onChange={(e) =>
                          updateItem(index, 'hours', e.target.value)
                        }
                        min="0.01"
                        step="0.25"
                        style={monoInput({
                          padding: '8px 10px',
                          background: 'var(--paper)',
                        })}
                      />
                      {/* The mechanic, visible while typing, before the tape
                          is even looked at. Positioned out of flow so it does
                          not make this column taller than Rate and Amount,
                          which the row bottom-aligns against. */}
                      <div
                        style={{
                          position: 'absolute',
                          top: '100%',
                          left: 0,
                          marginTop: '7px',
                        }}
                      >
                        {showsTally(qty) && tallySvg(qty, TALLY.screenSm)}
                      </div>
                    </div>
                    <div>
                      <label style={fieldLabel}>Rate</label>
                      <input
                        type="number"
                        value={item.rate}
                        onChange={(e) =>
                          updateItem(index, 'rate', e.target.value)
                        }
                        min="0.01"
                        step="0.01"
                        placeholder="0.00"
                        style={monoInput({
                          padding: '8px 10px',
                          background: 'var(--paper)',
                        })}
                      />
                    </div>
                    <div>
                      <label style={fieldLabel}>Amount</label>
                      <div
                        style={{
                          padding: '8px 10px',
                          fontSize: '15px',
                          color: 'var(--ink)',
                          background: 'var(--paper)',
                          border: '1px solid var(--rule-light)',
                          borderRadius: '4px',
                          textAlign: 'right',
                          fontFamily: 'var(--font-mono)',
                          fontWeight: 600,
                        }}
                      >
                        {money(qty * (parseFloat(item.rate) || 0), formData.currency)}
                      </div>
                    </div>
                    {formData.items.length > 1 && (
                      <button
                        onClick={() => removeItem(index)}
                        style={{
                          padding: '8px 12px',
                          background: 'transparent',
                          color: 'var(--ink-faint)',
                          border: '1px solid var(--rule-light)',
                          borderRadius: '4px',
                          cursor: 'pointer',
                          fontSize: '16px',
                          lineHeight: 1,
                        }}
                        aria-label={`Remove line item ${index + 1}`}
                      >
                        ×
                      </button>
                    )}
                  </div>
                </div>
              );
            })}
            <button
              onClick={addItem}
              className="tt-add"
              style={{
                width: '100%',
                padding: '12px',
                background: 'transparent',
                color: 'var(--ink-soft)',
                border: '1px dashed var(--rule)',
                borderRadius: '4px',
                cursor: 'pointer',
                fontSize: '14px',
                fontWeight: 500,
              }}
            >
              + Another thing
            </button>
          </div>

          {formDivider}

          {/* Notes */}
          {!showNotes && !formData.notes ? (
            <button onClick={() => setShowNotes(true)} style={quietButton}>
              + Add a note (payment terms, deadline, whatever)
            </button>
          ) : (
            <div>
              <div
                style={{
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'center',
                  marginBottom: '8px',
                }}
              >
                <label style={{ ...fieldLabel, marginBottom: 0 }}>Notes</label>
                <button
                  onClick={() => {
                    setShowNotes(false);
                    updateField('notes', '');
                  }}
                  style={{ ...quietButton, fontSize: '12px' }}
                >
                  Remove
                </button>
              </div>
              <textarea
                value={formData.notes}
                onChange={(e) => updateField('notes', e.target.value)}
                placeholder="Net 30? Thank you? Whatever you need to say..."
                rows={3}
                style={inputStyle({ resize: 'vertical' })}
              />
            </div>
          )}
        </div>

        {/* ---- Tape ---- */}
        <aside
          className="tt-tape-col"
          style={{
            flex: '0 0 auto',
            position: 'sticky',
            top: '28px',
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            gap: '20px',
          }}
        >
          {renderTape()}

          <button
            onClick={generatePDF}
            disabled={!valid}
            style={{
              width: `${SCREEN_TAPE_W}px`,
              padding: '14px 20px',
              background: 'var(--accent-fixed)',
              color: '#FFFFFF',
              border: 'none',
              borderRadius: '6px',
              cursor: valid ? 'pointer' : 'not-allowed',
              fontSize: '15px',
              fontWeight: 600,
              opacity: valid ? 1 : 0.42,
            }}
            onMouseOver={(e) => {
              if (valid) e.currentTarget.style.background = 'var(--accent-hover)';
            }}
            onMouseOut={(e) => {
              if (valid) e.currentTarget.style.background = 'var(--accent-fixed)';
            }}
          >
            {justSaved ? '✓ Done! Now get paid.' : 'Make it official'}
          </button>

          <p
            style={{
              fontSize: '13px',
              color: 'var(--text-tertiary)',
              margin: 0,
              maxWidth: `${SCREEN_TAPE_W}px`,
              textAlign: 'center',
              fontStyle: 'italic',
              minHeight: '20px',
            }}
          >
            {valid
              ? justSaved
                ? 'Now go get that money.'
                : ''
              : "(Just need the basics: who's paying whom, and for what.)"}
          </p>
        </aside>
      </div>

      {/* ---------------- Footer ---------------- */}
      <footer
        style={{
          width: '100%',
          maxWidth: '760px',
          marginTop: '72px',
          paddingTop: '28px',
          borderTop: '1px solid var(--border)',
          textAlign: 'center',
        }}
      >
        <p
          style={{
            fontSize: '14px',
            color: 'var(--text-secondary)',
            margin: '0 0 8px',
          }}
        >
          Zero accounts. Zero servers touching your data. Zero BS.
        </p>
        <p
          style={{
            fontSize: '13px',
            color: 'var(--text-tertiary)',
            margin: '0 0 12px',
          }}
        >
          A free tool from{' '}
          <a
            href="https://budmen.com"
            target="_blank"
            rel="noopener noreferrer"
            style={{
              color: 'var(--accent)',
              textDecoration: 'none',
              fontWeight: 500,
            }}
          >
            Budmen
          </a>
          , who also make other things.
        </p>
        <p
          style={{
            fontSize: '12px',
            color: 'var(--text-tertiary)',
            margin: '0 auto 16px',
            fontStyle: 'italic',
            maxWidth: '600px',
            lineHeight: 1.6,
          }}
        >
          We made this because invoicing shouldn't feel like a chore that
          requires signing up for yet another SaaS. Just a simple, honest tool
          that respects your time and privacy. For the independent makers,
          freelancers, and creators who'd rather spend their time making things
          than managing accounts.
        </p>
        <div
          style={{
            display: 'flex',
            gap: '16px',
            justifyContent: 'center',
            alignItems: 'center',
          }}
        >
          <a href="/about" className="tt-link">
            About
          </a>
          <span style={{ color: 'var(--border)' }}>•</span>
          <button
            className="tt-link"
            onClick={(e) => {
              const btn = e.currentTarget;
              navigator.clipboard.writeText(window.location.origin).then(() => {
                btn.textContent = 'Link copied!';
                setTimeout(() => {
                  btn.textContent = 'Share';
                }, 2000);
              });
            }}
          >
            Share
          </button>
          <span style={{ color: 'var(--border)' }}>•</span>
          <button
            className="tt-link"
            onClick={() => {
              localStorage.clear();
              window.location.reload();
            }}
          >
            Start fresh
          </button>
        </div>
      </footer>

      <style>{`
        .tt-form {
          box-shadow: 0 2px 12px rgba(40, 34, 28, 0.07);
        }

        /* The tape unspools from the top edge downward, as if printing.
           This is the only animation on the site. */
        .tape-anim {
          animation: unspool 720ms cubic-bezier(0.22, 0.61, 0.36, 1) both;
        }

        @keyframes unspool {
          from { clip-path: inset(0 0 100% 0); }
          to   { clip-path: inset(0 0 0 0); }
        }

        @keyframes slideDown {
          from { opacity: 0; max-height: 0; }
          to   { opacity: 1; max-height: 600px; }
        }

        .tt input::placeholder,
        .tt textarea::placeholder {
          color: var(--ink-faint);
          opacity: 1;
        }

        .tt input,
        .tt textarea {
          color-scheme: light;
        }

        .tt-add:hover {
          border-color: var(--accent-fixed);
          color: var(--accent-fixed);
        }

        .tt-link {
          font-size: 13px;
          color: var(--text-tertiary);
          background: none;
          border: none;
          padding: 0;
          cursor: pointer;
          text-decoration: none;
          font-family: inherit;
          transition: color 150ms ease;
        }
        .tt-link:hover { color: var(--accent); }

        .tape-mark { position: relative; display: inline-block; }
        .tape-mark-x {
          position: absolute;
          top: 50%;
          right: -18px;
          transform: translateY(-50%);
          background: none;
          border: none;
          padding: 0 4px;
          cursor: pointer;
          color: var(--ink-faint);
          font-size: 14px;
          line-height: 1;
          opacity: 0.35;
          transition: opacity 150ms ease, color 150ms ease;
        }
        .tape-mark:hover .tape-mark-x { opacity: 1; }
        .tape-mark-x:hover { color: var(--accent-fixed); }
        .tape-mark-restore {
          background: none;
          border: none;
          padding: 0;
          cursor: pointer;
          opacity: 0.55;
        }
        .tape-mark-restore:hover { opacity: 1; }

        @media (max-width: 980px) {
          .tt-columns { flex-direction: column; align-items: center; }
          .tt-form { width: 100%; flex: 1 1 auto; }
          .tt-tape-col { position: static; }
        }

        @media (max-width: 560px) {
          .tt-form { padding: 32px 22px 36px; }
          /* Side by side, the number field is too narrow to read at once. */
          .tt-meta { grid-template-columns: 1fr; gap: 16px; }
        }

        @media (prefers-reduced-motion: reduce) {
          /* Render the tape complete rather than animating it. */
          .tape-anim { animation: none !important; clip-path: none !important; }
        }
      `}</style>
    </div>
  );
}
